/**
 * Platform detection.
 *
 * Everything here exists because of one hard iOS rule: a web app only gets
 * push notifications, and only loses the browser chrome, once it has been
 * added to the home screen. The whole closure loop — "Joy has seen it",
 * "Paid ✓" — depends on it. So the app needs to know, reliably, whether it
 * is running installed or in a Safari tab.
 */

export type DisplayMode = 'standalone' | 'browser'

export function getDisplayMode(): DisplayMode {
  // The standards-track check, supported by Safari 16.4+.
  if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone'

  // Older iOS Safari only sets this non-standard property.
  const legacy = (window.navigator as Navigator & { standalone?: boolean }).standalone
  if (legacy === true) return 'standalone'

  return 'browser'
}

export function isInstalled(): boolean {
  return getDisplayMode() === 'standalone'
}

export function isIOS(): boolean {
  const ua = window.navigator.userAgent
  if (/iPad|iPhone|iPod/.test(ua)) return true
  // iPadOS 13+ reports itself as a Mac; the touch points give it away.
  return ua.includes('Macintosh') && navigator.maxTouchPoints > 1
}

/**
 * iOS version as [major, minor], or null if it can't be read.
 * Push requires 16.4 or later, so onboarding needs to be able to say
 * "this phone is too old" rather than silently failing.
 */
export function getIOSVersion(): [number, number] | null {
  const match = window.navigator.userAgent.match(/OS (\d+)_(\d+)/)
  if (!match) return null
  return [Number(match[1]), Number(match[2])]
}

export function supportsWebPush(): boolean {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false
  if (!isIOS()) return true

  // On iOS, push additionally requires the installed app and 16.4+.
  if (!isInstalled()) return false
  const v = getIOSVersion()
  if (!v) return true // can't tell; let the permission request be the judge
  const [major, minor] = v
  return major > 16 || (major === 16 && minor >= 4)
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  } catch (error) {
    console.error('Service worker registration failed', error)
    return null
  }
}
